import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import DatePickerButton from '../ui/date-picker-button';
import { Activity, RefreshCw } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import apiService from '../../services/api';
import { API_CONFIG } from '../../config/api';
const PerformanceHeatmap = ({
  title = "Performance Ratio Heatmap",
  startDate: propStartDate,
  endDate: propEndDate,
  selectedKPI
}) => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Set default range to 7 days (current date - 7 days to current date)
  const defaultEndDate = new Date();
  const defaultStartDate = new Date();
  defaultStartDate.setDate(defaultStartDate.getDate() - 7);
  const [startDate, setStartDate] = useState(propStartDate || defaultStartDate.toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(propEndDate || defaultEndDate.toISOString().split('T')[0]);
  const [selectedKpi, setSelectedKpi] = useState(selectedKPI || 'performance_ratio');
  
  // Update when props change
  useEffect(() => {
    if (propStartDate) setStartDate(propStartDate);
    if (propEndDate) setEndDate(propEndDate);
    if (selectedKPI) setSelectedKpi(selectedKPI);
  }, [propStartDate, propEndDate, selectedKPI]);
  const {
    toast
  } = useToast();
  const kpiOptions = [{
    value: 'performance_ratio',
    label: 'Performance Ratio (%)'
  }, {
    value: 'plant_availability',
    label: 'Plant Availability (%)'
  }, {
    value: 'grid_availability',
    label: 'Grid Availability (%)'
  }, {
    value: 'specific_yield',
    label: 'Specific Yield (kWh/kWp)'
  }, {
    value: 'specific_power',
    label: 'Specific Power (kW/kWp)'
  }, {
    value: 'temp_corr_ratio',
    label: 'Temp Corr PR (%)'
  }, {
    value: 'energy_export',
    label: 'Energy Export (kWh)'
  }, {
    value: 'energy_import',
    label: 'Energy Import (kWh)'
  }, {
    value: 'irradiation',
    label: 'Tilt POA Irradiation (kWh/m²)'
  }, {
    value: 'expected_energy',
    label: 'Expected Energy (kWh)'
  }, {
    value: 'dc_cuf_daily',
    label: 'DC CUF (%)'
  }, {
    value: 'ac_cuf_daily',
    label: 'AC CUF (%)'
  }];
  useEffect(() => {
    fetchHeatmapData();
  }, [startDate, endDate, selectedKpi]);
  const fetchHeatmapData = async () => {
    try {
      setIsLoading(true);
      const startDateTime = startDate + ' 00:00:00';
      const endDateTime = endDate + ' 23:59:59';
      console.log('Performance Heatmap: Fetching data with params:', {
        startDateTime,
        endDateTime,
        plant_id: API_CONFIG.PLANT_ID,
        selectedKpi
      });

      // Fetch KPI data
      const response = await apiService.getKpiReadingsByTags({
        start_date: startDateTime,
        end_date: endDateTime,
        plant_id: API_CONFIG.PLANT_ID,
        asset_id_list: [API_CONFIG.PLANT_ID],
        tags_list: [selectedKpi]
      });
      console.log('Performance Heatmap: API Response:', response);
      if (response.data) {
        // Group data by day and calculate daily averages
        const dailyData = {};
        const observedArray = Array.isArray(response.data) ? response.data : response.data?.body || [];
        observedArray.forEach(item => {
          if (item.readings && item.readings[0] && item.readings[0].KPI) {
            const kpiReading = item.readings[0].KPI.find(kpi => kpi[selectedKpi]);
            if (kpiReading) {
              const value = kpiReading[selectedKpi];
              const date = new Date(item.timestamp).toISOString().split('T')[0]; // Get date only

              if (!dailyData[date]) {
                dailyData[date] = [];
              }
              dailyData[date].push(value);
            }
          }
        });

        // Calculate daily averages and create heatmap data
        const processedData = [];

        // Get all dates in the range and sort them
        const allDates = Object.keys(dailyData).sort();
        const startDateObj = new Date(startDate);
        const endDateObj = new Date(endDate);

        // Generate all dates in the range
        const dateRange = [];
        const currentDate = new Date(startDateObj);
        while (currentDate <= endDateObj) {
          dateRange.push(currentDate.toISOString().split('T')[0]);
          currentDate.setDate(currentDate.getDate() + 1);
        }

        // Process each date in the range
        dateRange.forEach((date, index) => {
          const dailyValues = dailyData[date] || [];
          let averageValue = 0;
          if (dailyValues.length > 0) {
            averageValue = dailyValues.reduce((sum, val) => sum + val, 0) / dailyValues.length;
          }
          const dayOfWeek = new Date(date).getDay(); // 0-6 (Sunday-Saturday)
          const weekNumber = Math.floor(index / 7); // Calculate week number

          // Determine color based on value and KPI type
          let color = '#EF4444'; // Red for low performance

          if (selectedKpi === 'performance_ratio' || selectedKpi === 'plant_availability' || selectedKpi === 'grid_availability' || selectedKpi === 'temp_corr_ratio' || selectedKpi === 'dc_cuf_daily' || selectedKpi === 'ac_cuf_daily') {
            // Percentage-based KPIs
            if (averageValue >= 88) {
              color = '#10B981'; // Green for high performance
            } else if (averageValue >= 86) {
              color = '#F59E0B'; // Orange for medium performance
            } else if (averageValue >= 84) {
              color = '#F97316'; // Dark orange for low-medium performance
            }
          } else if (selectedKpi === 'specific_yield') {
            // Specific Yield (kWh/kWp) - typical range 3-5
            if (averageValue >= 4.5) {
              color = '#10B981'; // Green for high performance
            } else if (averageValue >= 4.0) {
              color = '#F59E0B'; // Orange for medium performance
            } else if (averageValue >= 3.5) {
              color = '#F97316'; // Dark orange for low-medium performance
            }
          } else if (selectedKpi === 'specific_power') {
            // Specific Power (kW/kWp) - typical range 0.7-0.9
            if (averageValue >= 0.85) {
              color = '#10B981'; // Green for high performance
            } else if (averageValue >= 0.80) {
              color = '#F59E0B'; // Orange for medium performance
            } else if (averageValue >= 0.75) {
              color = '#F97316'; // Dark orange for low-medium performance
            }
          } else {
            // Energy-based KPIs - use a different threshold
            const maxValue = Math.max(...Object.values(dailyData).map(values => values.reduce((sum, val) => sum + val, 0) / values.length));
            if (averageValue >= maxValue * 0.9) {
              color = '#10B981'; // Green for high performance
            } else if (averageValue >= maxValue * 0.8) {
              color = '#F59E0B'; // Orange for medium performance
            } else if (averageValue >= maxValue * 0.7) {
              color = '#F97316'; // Dark orange for low-medium performance
            }
          }
          const formattedDate = new Date(date).toLocaleDateString('en-US', {
            day: 'numeric',
            month: 'short'
          });
          processedData.push({
            x: dayOfWeek,
            y: weekNumber,
            value: Number(averageValue.toFixed(2)),
            date: formattedDate,
            color
          });
        });
        console.log('Performance Heatmap: Processed Data:', processedData);
        setData(processedData);
      }
    } catch (error) {
      console.error('Error fetching heatmap data:', error);
      toast({
        title: "Error",
        description: "Failed to fetch heatmap data. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };
  const formatNumber = num => {
    return new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 2
    }).format(num);
  };
  const getDayName = dayIndex => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return days[dayIndex];
  };
  const getColorLegend = () => {
    let legendItems = [];
    if (selectedKpi === 'performance_ratio' || selectedKpi === 'plant_availability' || selectedKpi === 'grid_availability' || selectedKpi === 'temp_corr_ratio' || selectedKpi === 'dc_cuf_daily' || selectedKpi === 'ac_cuf_daily') {
      // Percentage-based KPIs
      legendItems = [{
        color: '#10B981',
        label: 'Excellent (≥88%)'
      }, {
        color: '#F59E0B',
        label: 'Good (86-88%)'
      }, {
        color: '#F97316',
        label: 'Low (84-86%)'
      }, {
        color: '#EF4444',
        label: 'Critical (<84%)'
      }];
    } else if (selectedKpi === 'specific_yield') {
      // Specific Yield
      legendItems = [{
        color: '#10B981',
        label: 'Excellent (≥4.5 kWh/kWp)'
      }, {
        color: '#F59E0B',
        label: 'Good (4.0-4.5 kWh/kWp)'
      }, {
        color: '#F97316',
        label: 'Low (3.5-4.0 kWh/kWp)'
      }, {
        color: '#EF4444',
        label: 'Critical (<3.5 kWh/kWp)'
      }];
    } else if (selectedKpi === 'specific_power') {
      // Specific Power
      legendItems = [{
        color: '#10B981',
        label: 'Excellent (≥0.85 kW/kWp)'
      }, {
        color: '#F59E0B',
        label: 'Good (0.80-0.85 kW/kWp)'
      }, {
        color: '#F97316',
        label: 'Low (0.75-0.80 kW/kWp)'
      }, {
        color: '#EF4444',
        label: 'Critical (<0.75 kW/kWp)'
      }];
    } else {
      // Energy-based KPIs
      legendItems = [{
        color: '#10B981',
        label: 'Excellent (≥90% of max)'
      }, {
        color: '#F59E0B',
        label: 'Good (80-90% of max)'
      }, {
        color: '#F97316',
        label: 'Low (70-80% of max)'
      }, {
        color: '#EF4444',
        label: 'Critical (<70% of max)'
      }];
    }
    return <div className="flex flex-wrap justify-center gap-4 mt-4">
        {legendItems.map((item, index) => <div key={index} className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full" style={{
          backgroundColor: item.color
        }}></div>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {item.label}
            </span>
          </div>)}
      </div>;
  };
  const CustomTooltip = ({
    active,
    payload,
    label
  }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return <div className="bg-white dark:bg-gray-800 p-2 rounded-md shadow-lg">
          <p className="text-sm text-gray-900 dark:text-white">{label}</p>
          <p className="text-lg font-bold text-blue-600">
            {formatNumber(data.value)}
          </p>
        </div>;
    }
    return null;
  };
  return <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center space-x-2">
            <Activity className="h-5 w-5 text-blue-500" />
            <span>{title}</span>
          </CardTitle>
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm" onClick={fetchHeatmapData} disabled={isLoading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Controls */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              KPI to Show
            </label>
            <select value={selectedKpi} onChange={e => setSelectedKpi(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white">
              {kpiOptions.map(option => <option key={option.value} value={option.value}>
                  {option.label}
                </option>)}
            </select>
          </div>
          <div>
            <DatePickerButton selectedDate={startDate} onDateChange={setStartDate} label="Start Date" placeholder="Select Start Date" />
          </div>
          <div>
            <DatePickerButton selectedDate={endDate} onDateChange={setEndDate} label="End Date" placeholder="Select End Date" />
          </div>
        </div>

        {/* Heatmap */}
        {isLoading ? <div className="space-y-4">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-64 w-full" />
          </div> : data.length > 0 ? <div className="space-y-4">
            {/* Heatmap Grid */}
            <div className="flex flex-col space-y-2">
              {/* Day labels */}
              <div className="flex justify-center space-x-2">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => <div key={day} className="w-12 h-8 flex items-center justify-center text-xs font-medium text-gray-600 dark:text-gray-400">
                    {day}
                  </div>)}
              </div>
              
              {/* Calculate number of weeks needed */}
              {(() => {
            const startDateObj = new Date(startDate);
            const endDateObj = new Date(endDate);
            const daysDiff = Math.ceil((endDateObj.getTime() - startDateObj.getTime()) / (1000 * 60 * 60 * 24));
            const weeksNeeded = Math.ceil(daysDiff / 7);
            return Array.from({
              length: weeksNeeded
            }, (_, weekIndex) => <div key={weekIndex} className="grid grid-cols-7 gap-1 max-w-md mx-auto">
                    {Array.from({
                length: 7
              }, (_, dayIndex) => {
                const dayData = data.find(item => item.x === dayIndex && item.y === weekIndex);
                return <div key={dayIndex} className="w-12 h-12 rounded-lg border border-gray-200 dark:border-gray-700 flex items-center justify-center text-xs font-medium text-white cursor-pointer hover:opacity-80 transition-opacity" style={{
                  backgroundColor: dayData ? dayData.color : '#f3f4f6',
                  color: dayData ? 'white' : '#6b7280'
                }} title={dayData ? `${dayData.date}: ${formatNumber(dayData.value)}` : 'No data'}>
                          {dayData ? formatNumber(dayData.value) : '-'}
                        </div>;
              })}
                  </div>);
          })()}
            </div>

            {/* Color Legend */}
            {getColorLegend()}

            {/* Summary Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
              <Card>
                <CardContent className="p-4">
                  <div className="text-center">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Average</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {formatNumber(data.reduce((sum, item) => sum + item.value, 0) / data.length)}
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="text-center">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Best Day</p>
                    <p className="text-2xl font-bold text-green-600">
                      {formatNumber(Math.max(...data.map(item => item.value)))}
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="text-center">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Worst Day</p>
                    <p className="text-2xl font-bold text-red-600">
                      {formatNumber(Math.min(...data.map(item => item.value)))}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div> : <div className="text-center py-8">
            <Activity className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">No heatmap data available</p>
          </div>}
      </CardContent>
    </Card>;
};
export default PerformanceHeatmap;